import React, { useMemo } from 'react';
import { DndContext, closestCorners, useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';

const STATUS_CONFIG = {
  'to-do': {
    label: 'To Do',
    color: '#7C3AED',
    border: 'linear-gradient(90deg, #7C3AED 0%, #9F7AEA 100%)',
    dot: '#7C3AED',
  },
  'in-progress': {
    label: 'On Progress',
    color: '#F59E0B',
    border: 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)',
    dot: '#F59E0B',
  },
  'pending-approval': {
    label: 'Pending Approval',
    color: '#0EA5E9',
    border: 'linear-gradient(90deg, #0EA5E9 0%, #22D3EE 100%)',
    dot: '#0EA5E9',
  },
  completed: {
    label: 'Completed',
    color: '#10B981',
    border: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)',
    dot: '#10B981',
  },
  closed: {
    label: 'Closed',
    color: '#94A3B8',
    border: 'linear-gradient(90deg, #94A3B8 0%, #CBD5E1 100%)',
    dot: '#94A3B8',
  },
};

const PRIORITY_COLOR = {
  low: '#22C55E',
  medium: '#F97316',
  high: '#EF4444',
};

function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(d);
}

function TaskCard({ task, columnId }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: task.id,
    data: { column: columnId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const priority = (task.priority || 'medium').toLowerCase();
  const statusKey = String(task.status || 'to-do').toLowerCase();
  const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG['to-do'];
  const priorityColor = PRIORITY_COLOR[priority] || PRIORITY_COLOR.medium;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${statusCfg.color}`,
          background: '#fff',
          boxShadow: '0 14px 32px rgba(17,24,39,0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Chip
            label={task.project_name || 'Project'}
            size="small"
            sx={{
              bgcolor: 'rgba(124,58,237,0.08)',
              color: '#6D28D9',
              borderRadius: 1,
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          />
          <Chip
            label={statusCfg.label}
            size="small"
            sx={{
              bgcolor: `${statusCfg.color}1a`,
              color: statusCfg.color,
              fontWeight: 700,
              borderRadius: 1,
              textTransform: 'capitalize',
            }}
          />
        </Box>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: '#0F172A', mb: 0.5 }}
        >
          {task.title || 'Untitled task'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {task.description || 'No description provided.'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            label={(task.priority || 'Medium').toString().replace(/^\w/, (c) => c.toUpperCase())}
            size="small"
            sx={{
              bgcolor: `${priorityColor}1a`,
              color: priorityColor,
              fontWeight: 700,
              borderRadius: 1,
            }}
          />
          {task.due_date && (
            <Chip
              label={`Due ${formatDate(task.due_date)}`}
              size="small"
              sx={{
                bgcolor: 'rgba(59,130,246,0.08)',
                color: '#2563EB',
                fontWeight: 600,
                borderRadius: 1,
              }}
            />
          )}
        </Box>

        {task.tags && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {Array.isArray(task.tags)
              ? task.tags
                  .filter(Boolean)
                  .map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(15,118,110,0.08)',
                        color: '#0F766E',
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                    />
                  ))
              : String(task.tags)
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean)
                  .map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(15,118,110,0.08)',
                        color: '#0F766E',
                        borderRadius: 999,
                        fontWeight: 600,
                      }}
                    />
                  ))}
          </Box>
        )}
      </Paper>
    </div>
  );
}

function BoardColumn({ columnId, config, cards }) {
  const { setNodeRef } = useDroppable({
    id: columnId,
    data: { column: columnId },
  });

  return (
    <Box
      sx={{
        flex: '1 1 280px',
        minWidth: 260,
        maxWidth: 340,
        bgcolor: 'rgba(248,250,252,0.8)',
        borderRadius: 2,
        border: '1px solid rgba(226,232,240,0.8)',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        boxShadow: '0 10px 24px rgba(15,23,42,0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: -0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: config.dot,
            }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {config.label}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {cards.length}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 3,
          borderRadius: 999,
          background: config.border,
          mb: 0.5,
        }}
      />

      <Box ref={setNodeRef}>
        <SortableContext items={cards.map((c) => c.id)} strategy={rectSortingStrategy}>
          {cards.map((card) => (
            <TaskCard key={card.id} task={card} columnId={columnId} />
          ))}
        </SortableContext>

        {cards.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              borderStyle: 'dashed',
              color: 'text.secondary',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="body2">No tasks here yet.</Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

function BoardView({ tasks = [], onUpdateStatus }) {
  const columns = useMemo(() => {
    const grouped = {
      'to-do': [],
      'in-progress': [],
      'pending-approval': [],
      completed: [],
      closed: [],
    };

    (tasks || []).forEach((t) => {
      const key = String(t.status || 'to-do').toLowerCase();
      if (key === 'completed' && !t.archived) {
        grouped['pending-approval'].push(t);
        return;
      }
      if (grouped[key]) grouped[key].push(t);
      else grouped['to-do'].push(t);
    });

    return grouped;
  }, [tasks]);

  const columnOrder = ['to-do', 'in-progress', 'pending-approval', 'completed', 'closed'];

  const findColumnForCard = (cardId) => {
    return columnOrder.find((col) => columns[col].some((c) => c.id === cardId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sourceColumn = findColumnForCard(active.id);
    const targetColumn =
      over.data?.current?.column ||
      findColumnForCard(over.id);

    if (targetColumn && sourceColumn && targetColumn !== sourceColumn) {
      onUpdateStatus(active.id, targetColumn);
    }
  };

  return (
    <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          overflowX: 'auto',
          pb: 1,
          pt: 1,
        }}
      >
        {columnOrder.map((colId) => (
          <BoardColumn
            key={colId}
            columnId={colId}
            config={STATUS_CONFIG[colId]}
            cards={columns[colId]}
          />
        ))}
      </Box>
    </DndContext>
  );
}

export default BoardView;
